import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostBinding, inject, Input, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { CheckboxListItemDirective } from './checkbox-list-item/checkbox-list-item.directive'
import { CheckboxOption } from './checkbox-option'
import { FieldComponent } from '../field/field.component'

/**
 * @public
 */
export type CheckboxListElement = NgElement & WithProperties<CheckboxListComponent>

/**
 * @public
 *
 * @todo {@link https://jira.gpei.ca/browse/API-4075 API-4075}
 */
@Component({
  selector: 'gpei-checkbox-list',
  templateUrl: './checkbox-list.component.html',
  styleUrls: [ './checkbox-list.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule,
    CheckboxListItemDirective
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ]
})
export class CheckboxListComponent extends FieldComponent {

  private static readonly VALIDITY_MESSAGES = {
    valueMissing: 'Please select an item in the list.'
  }

  @ViewChild('list')
  private _listRef: ElementRef<HTMLDivElement>

  @ViewChildren('checkbox')
  private _checkboxRefs: QueryList<ElementRef<HTMLInputElement>>

  private _options: CheckboxOption[] = []

  private _showSelectAll: boolean

  protected _value: string[] = []

  protected _elementRef = inject(ElementRef)

  /**
   * The label that will be displayed alongside the input
   */
  @Input()
  @HostBinding()
  label: string

  /**
   * The description that will be displayed alongside the input
   */
  @Input()
  @HostBinding()
  description: string

  /**
   * Toggles the selection state of all checkboxes based on the selectAll checkbox state.
   *
   * @param event - The change event is triggered by the selectAll checkbox.
   */
  onSelectAllCheckboxChanged(event: Event): void {
    // Extracts the checked state of the selectAll checkbox from the event
    let checked = (event.target as HTMLInputElement).checked
    this._options.forEach((item, index) => {
      item.selected = checked
      let pendingValue = [...this._value]
      let listItemValue = checked ? item.checkedValue : ""
      pendingValue[index] = listItemValue

      this.writeValue(pendingValue)

      // Mark it as touched
      this.touched = true

      // Update the validity of the element
      this.onValidate(this._value)
    })
    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Dispatch a new input event
    this._elementRef.nativeElement.dispatchEvent(new InputEvent('input', { ...event, bubbles: true }))
  }

  /**
   * Lifecycle hook for when the slotted content of the component is updated.
   *
   * This will update the options that are available to be selected from the checkbox list.
   *
   * @param event The DOM event that triggered this hook
   */
  onSlottedContentChanged(event: Event): void {
    let slot = event.target as HTMLSlotElement

    // Update the list of options
    let pendingOptions = []
    slot?.assignedElements()?.forEach(it => {
      let label = it?.getAttribute('label')

      // If there is a label add it to the list of options
      if (label) {
        let checkedValue = it?.getAttribute('checked-value') ?? it?.getAttribute('value') ?? true
        let uncheckedValue = it?.getAttribute('unchecked-value') ?? false

        let selected = it?.getAttribute('selected') ?? it?.getAttribute('checked') ?? false
        if (typeof selected === 'string') {
          selected = selected?.toLowerCase() !== 'false'
        }

        pendingOptions.push({
          label,
          checkedValue,
          uncheckedValue,
          selected
        })
      }
    })

    this._options = pendingOptions

    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Set the initial value
    let pendingValue = this._checkboxRefs?.map(it => it.nativeElement.value)
    this.writeValue(pendingValue)

    // Validate the initial value
    this.onValidate(this._value)
  }

  /**
   * Lifecycle hook for when the container that holds the checkbox is clicked.
   *
   * Passes through the click event to the target input element.
   *
   * @param event The triggering click event
   */
  onListItemClicked(event: MouseEvent): void {
    // If the target is either the child label or input element, carry on as normal
    if (event.target instanceof HTMLLabelElement || event.target instanceof HTMLInputElement) {
      return
    }

    // Else simulate a click event
    event.preventDefault()
    let listItemElement = event.currentTarget as HTMLLIElement

    let inputElement = listItemElement.querySelector<HTMLInputElement>('input')
    inputElement.click()
  }

  /**
   * Lifecycle hook for when a checkbox inside the list has been clicked.
   *
   * It will set the checkbox inside as checked and update the value of the field.
   *
   * @param event The event that triggered the hook
   */
  onInput(event: Event): void {
    // Stop propagation of original click event
    event.stopPropagation()

    // Update the value
    let target = (event as InputEvent).target as HTMLInputElement
    let index = Number.parseInt(target.name)

    let pendingValue = [...this._value]
    pendingValue[index] = target.value

    this.writeValue(pendingValue)

    // Mark it as touched
    this.touched = true

    // Update the validity of the element
    this.onValidate(this._value)

    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Dispatch a new input event
    this._elementRef.nativeElement.dispatchEvent(new InputEvent('input', { ...event, bubbles: true }))
  }

  /**
   * Lifecycle hook for when the validation of the input is trigggered.
   *
   * It will update the validity state and the message accordingly if any of the validation fails.
   *
   * @param value The value of the component that will be validated.
   */
  onValidate(value: string[]): void {
    let pendingValidityStateFlags = {}

    // If required check if one or more checkboxes have the checked value
    if (this.required) {
      pendingValidityStateFlags = {
        ...pendingValidityStateFlags,
        ...this.validateRequired(value)
      }
    }

    let validityMessage = this.determineValidityMessage(pendingValidityStateFlags)

    this.setValidity(pendingValidityStateFlags, validityMessage, this._listRef.nativeElement)
  }

  /**
   * Find the first matching validity message for the provided validity state.
   *
   * @param state The state that will be used to determine the validatity message
   *
   * @returns     String value of the found message, else it will return `NULL`.
   */
  determineValidityMessage(state: ValidityStateFlags): string {
    let key = Object
      .keys(state)
      .find(it => Object.hasOwn(CheckboxListComponent.VALIDITY_MESSAGES, it))

    return CheckboxListComponent.VALIDITY_MESSAGES?.[key]
  }

  /**
   * Check if one or more of the checkboxes are checked.
   *
   * @param value The value that is being checked
   *
   * @returns Empty collection of validity flags if it passes the validation. If it fails the validation
   *          it will return a validity flag collection of `{ valueMissing: true }`
   */
  validateRequired(value: string[]): ValidityStateFlags {
    let hasSomeChecked = value.some((it, index) => it == this._options[index].checkedValue)
    return hasSomeChecked ?
      {} :
      { valueMissing: true }
  }

  writeValue(value: string | string[]): void {
    let pendingValue = typeof value === 'string' ? value : JSON.stringify(value)
    super.writeValue(pendingValue)
  }

  /**
   * Assign a new value to the control of the component
   *
   * @param value   A raw value that will be normalized before saving
   */
  @Input()
  @HostBinding()
  set value(value: string) {
    let pendingValue = this._value
    try {
      pendingValue = JSON.parse(value) as string[]
    }
    catch {
      pendingValue = value.split(',')
    }

    if (this._value !== pendingValue) {
      this._value = pendingValue
      this.internals?.setFormValue(value as string)
    }
  }

  /**
   * Get the current normalized value from of the component
   */
  get value(): string {
    return this?._value.join()
  }

  /**
   * A collection of options for the checkbox list
   */
  get options(): CheckboxOption[] {
    return this?._options ?? []
  }

  /**
   * The showSelectAll option will enable or disable the selectAll checkbox.
   */
   @Input()
   @HostBinding()
   set showSelectAll(value: boolean | string) {
       let isSelectAll = value

     if (typeof isSelectAll === 'string') {
       isSelectAll = isSelectAll.toLowerCase() !== 'false'
     }

     this._showSelectAll = isSelectAll
   }

   get showSelectAll(): boolean {
     return this._showSelectAll
   }

}

/**
 * @public
 */
export let registerCheckboxListElement = registerCustomElementFactory(CheckboxListComponent)
